import { MapMarker, MapPolygon, MapPolyline, MapPosition } from './types'

export interface BoundaryFitOptions {
  animate?: boolean
  maxZoom?: number
  minZoom?: number
  padding?: google.maps.Padding | number
}

export interface BoundaryFitResult {
  bounds: google.maps.LatLngBounds
  center: google.maps.LatLng
  zoom?: number
}

/**
 * Creates bounds from an array of positions
 */
export const createBoundsFromPositions = (
  positions: MapPosition[]
): google.maps.LatLngBounds => {
  const bounds = new google.maps.LatLngBounds()
  positions.forEach((position) => {
    bounds.extend(new google.maps.LatLng(position.lat, position.lng))
  })
  return bounds
}

/**
 * Creates bounds from a polygon's paths
 */
export const createBoundsFromPolygon = (
  polygon: MapPolygon
): google.maps.LatLngBounds => {
  return createBoundsFromPositions(polygon.paths)
}

/**
 * Creates bounds from a polyline's path
 */
export const createBoundsFromPolyline = (
  polyline: MapPolyline
): google.maps.LatLngBounds => {
  return createBoundsFromPositions(polyline.path)
}

/**
 * Creates bounds from markers
 */
export const createBoundsFromMarkers = (
  markers: MapMarker[]
): google.maps.LatLngBounds => {
  const positions = markers.map((marker) => marker.position)
  return createBoundsFromPositions(positions)
}

/**
 * Fits map bounds to a polygon with optional configuration
 */
export const fitBoundsToPolygon = (
  map: google.maps.Map,
  polygon: MapPolygon,
  options: BoundaryFitOptions = {}
): BoundaryFitResult => {
  const bounds = createBoundsFromPolygon(polygon)
  return fitBoundsToMap(map, bounds, options)
}

/**
 * Fits map bounds to a polyline with optional configuration
 */
export const fitBoundsToPolyline = (
  map: google.maps.Map,
  polyline: MapPolyline,
  options: BoundaryFitOptions = {}
): BoundaryFitResult => {
  const bounds = createBoundsFromPolyline(polyline)
  return fitBoundsToMap(map, bounds, options)
}

/**
 * Fits map bounds to markers with optional configuration
 */
export const fitBoundsToMarkers = (
  map: google.maps.Map,
  markers: MapMarker[],
  options: BoundaryFitOptions = {}
): BoundaryFitResult => {
  const bounds = createBoundsFromMarkers(markers)
  return fitBoundsToMap(map, bounds, options)
}

/**
 * Fits map bounds to multiple polygons with optional configuration
 */
export const fitBoundsToPolygons = (
  map: google.maps.Map,
  polygons: MapPolygon[],
  options: BoundaryFitOptions = {}
): BoundaryFitResult => {
  const bounds = new google.maps.LatLngBounds()
  polygons.forEach((polygon) => {
    const polygonBounds = createBoundsFromPolygon(polygon)
    bounds.union(polygonBounds)
  })
  return fitBoundsToMap(map, bounds, options)
}

/**
 * Fits map bounds to multiple polylines with optional configuration
 */
export const fitBoundsToPolylines = (
  map: google.maps.Map,
  polylines: MapPolyline[],
  options: BoundaryFitOptions = {}
): BoundaryFitResult => {
  const bounds = new google.maps.LatLngBounds()
  polylines.forEach((polyline) => {
    const polylineBounds = createBoundsFromPolyline(polyline)
    bounds.union(polylineBounds)
  })
  return fitBoundsToMap(map, bounds, options)
}

/**
 * Fits map bounds to a combination of markers, polygons, and polylines
 */
export const fitBoundsToAll = (
  map: google.maps.Map,
  data: {
    markers?: MapMarker[]
    polygons?: MapPolygon[]
    polylines?: MapPolyline[]
  },
  options: BoundaryFitOptions = {}
): BoundaryFitResult => {
  const bounds = new google.maps.LatLngBounds()

  if (data.markers) {
    const markerBounds = createBoundsFromMarkers(data.markers)
    bounds.union(markerBounds)
  }

  if (data.polygons) {
    data.polygons.forEach((polygon) => {
      const polygonBounds = createBoundsFromPolygon(polygon)
      bounds.union(polygonBounds)
    })
  }

  if (data.polylines) {
    data.polylines.forEach((polyline) => {
      const polylineBounds = createBoundsFromPolyline(polyline)
      bounds.union(polylineBounds)
    })
  }

  return fitBoundsToMap(map, bounds, options)
}

/**
 * Core function to fit bounds to map with configuration options
 */
export const fitBoundsToMap = (
  map: google.maps.Map,
  bounds: google.maps.LatLngBounds,
  options: BoundaryFitOptions = {}
): BoundaryFitResult => {
  const { animate = true, maxZoom, minZoom, padding = 50 } = options

  // Calculate the center before fitting bounds
  const center = bounds.getCenter()

  // Fit bounds with padding
  map.fitBounds(bounds, padding)

  // Apply zoom constraints if provided
  if (maxZoom || minZoom) {
    const currentZoom = map.getZoom() || 10
    let targetZoom = currentZoom

    if (maxZoom && currentZoom > maxZoom) {
      targetZoom = maxZoom
    }
    if (minZoom && currentZoom < minZoom) {
      targetZoom = minZoom
    }

    if (targetZoom !== currentZoom) {
      if (animate) {
        map.setZoom(targetZoom)
      } else {
        map.setZoom(targetZoom)
      }
    }
  }

  return {
    bounds,
    center,
    zoom: map.getZoom() || undefined
  }
}

/**
 * Smoothly pans to a position and steps the zoom level towards a target,
 * one level at a time, to produce an animated zoom-in/out effect.
 */
const POSITION_EPSILON = 1e-6

const lastAnimatedTargetByMap = new WeakMap<
  google.maps.Map,
  { lat: number; lng: number; zoom: number }
>()

export const animateMapToPosition = (
  map: google.maps.Map,
  position: MapPosition,
  targetZoom: number,
  options: { stepDelay?: number } = {}
): void => {
  const { stepDelay = 40 } = options

  const lastTarget = lastAnimatedTargetByMap.get(map)
  const isSameAsLastTarget =
    lastTarget != null &&
    Math.abs(lastTarget.lat - position.lat) < POSITION_EPSILON &&
    Math.abs(lastTarget.lng - position.lng) < POSITION_EPSILON &&
    lastTarget.zoom === targetZoom

  if (isSameAsLastTarget) return

  lastAnimatedTargetByMap.set(map, {
    lat: position.lat,
    lng: position.lng,
    zoom: targetZoom
  })

  map.panTo(position)

  const currentZoom = map.getZoom()
  if (currentZoom == null || currentZoom === targetZoom) return

  const step = currentZoom < targetZoom ? 1 : -1
  let zoom = currentZoom

  const tick = () => {
    zoom += step
    map.setZoom(zoom)
    if (zoom !== targetZoom) {
      setTimeout(tick, stepDelay)
    }
  }

  setTimeout(tick, stepDelay)
}

/**
 * Utility to check if bounds are valid (not empty)
 */
export const isValidBounds = (bounds: google.maps.LatLngBounds): boolean => {
  const ne = bounds.getNorthEast()
  const sw = bounds.getSouthWest()
  return ne.lat() !== sw.lat() || ne.lng() !== sw.lng()
}

/**
 * Utility to get bounds center
 */
export const getBoundsCenter = (
  bounds: google.maps.LatLngBounds
): google.maps.LatLng => {
  return bounds.getCenter()
}

/**
 * Utility to get bounds size (width and height in degrees)
 */
export const getBoundsSize = (
  bounds: google.maps.LatLngBounds
): { height: number; width: number } => {
  const ne = bounds.getNorthEast()
  const sw = bounds.getSouthWest()
  return {
    height: ne.lat() - sw.lat(),
    width: ne.lng() - sw.lng()
  }
}
